// ─────────────────────────────────────────────────────────────────────────────
// src/lib/adsenseLoader.js
//
// Global AdSense script loader. Ensures the adsbygoogle.js script is loaded
// exactly ONCE regardless of how many ad components mount or how many times
// CookieConsentBanner calls loadAdvertisingScript(). Every ad component should
// call `ensureAdsenseLoaded()` instead of injecting its own <script>.
//
// AdSense client ID is read from VITE_ADSENSE_CLIENT.
// ─────────────────────────────────────────────────────────────────────────────

const ADSENSE_CLIENT = import.meta.env.VITE_ADSENSE_CLIENT || "ca-pub-4587131507169333";
const SCRIPT_ID = "marstick-google-adsense";
const SCRIPT_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;

let loadPromise = null;

/**
 * Ensures the AdSense script is loaded exactly once.
 * Returns a Promise that resolves when the script is ready.
 * Safe to call from multiple components simultaneously.
 */
export function ensureAdsenseLoaded() {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve) => {

    // Already loaded by a previous consent-approved call?
    const existing =
      document.getElementById(SCRIPT_ID) ||
      document.querySelector(`script[src*="adsbygoogle.js"][src*="${ADSENSE_CLIENT}"]`);

    if (existing) {
      // Tag the existing script so future checks find it by ID
      if (!existing.id) existing.id = SCRIPT_ID;
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.setAttribute("data-ad-client", ADSENSE_CLIENT);
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Safely push an ad slot to AdSense's queue.
 * Handles the case where adsbygoogle isn't available yet.
 * Returns true if the push succeeded, false otherwise.
 */
export function pushAdSlot() {
  try {
    if (typeof window === "undefined") return false;
    window.adsbygoogle = window.adsbygoogle || [];
    window.adsbygoogle.push({});
    return true;
  } catch {
    return false;
  }
}

export { ADSENSE_CLIENT };